/* qeslint-disable */

import { LayoutParameters } from "../Settings";
import { Theme } from "../theme";
import { utils, proxyCtx } from "../utils/utils";
import { handleDrag } from "../utils/util_handle_drag";
import Diamond from "./Diamond";
import { DataStore } from "../utils/util_datastore";
import { Dispatcher } from "../utils/util_dispatcher";
import { VideoRecorder } from "../VideoRecorder";

const LINE_HEIGHT = LayoutParameters.LINE_HEIGHT;
const TIME_SCROLLER_HEIGHT = 35;
export const MARKER_TRACK_HEIGHT = 25;

let timeScale = LayoutParameters.time_scale;

let frameStart = 0; // this is the current scroll position.

/*
 * This class contains the view for the right main section of timeliner
 */

// TODO
// dirty rendering
// drag block
// DON'T use time.update for everything

let tickMark1: number;
let tickMark2: number;
let tickMark3: number;

function timeScaled(): void {
    /*
     * Subdivison LOD
     * time_scale refers to number of pixels per unit
     * Eg. 1 inch - 60s, 1 inch - 60fps, 1 inch - 6 mins
     */
    const div = 60;

    tickMark1 = timeScale / div;
    tickMark2 = 2 * tickMark1;
    tickMark3 = 10 * tickMark1;
}

timeScaled();

/** *********************** */
// Timeline Panel
/** *********************** */

interface Pointer {
    x: number;
    y: number;
    offsetx?: number;
    offsety?: number;
    [key: string]: any;
}

interface Frame {
    time: number;
    tween: string;
    value: number;
}

export class TimelinePanel {
    dom: HTMLElement;
    private trackCanvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private ctxWrap: ReturnType<typeof proxyCtx>;
    private dataStore: DataStore;
    private dispatcher: Dispatcher;
    private videoRecorder: VideoRecorder;
    private scrollTop: number = 0;
    private scrollLeft: number = 0;
    private SCROLL_HEIGHT: number = 0;
    private layers: any[] = [];
    private currentTime: number = 0; // measured in seconds
    private needsRepaint: boolean = false;
    private renderItems: Diamond[] = [];
    private over: Diamond | null = null;
    private mousedownItem: Diamond | null = null;
    private pointer: Pointer | null = null;
    private mousedown2: boolean = false;
    private mouseDownThenMove: boolean = false;
    private pointerDidMoved: boolean = false;
    private canvasBounds: DOMRect | null = null;

    constructor(dataStore: DataStore, dispatcher: Dispatcher, videoRecorder: VideoRecorder) {
        this.dataStore = dataStore;
        this.dispatcher = dispatcher;
        this.videoRecorder = videoRecorder;

        const trackCanvas = document.createElement("canvas");

        this.trackCanvas = trackCanvas;

        utils.style(trackCanvas, {
            position: "absolute",
            top: "0px",
            left: "0px"
        });

        const div = document.createElement("div");

        div.appendChild(trackCanvas);

        trackCanvas.id = "track-canvas";

        this.dom = div;
        this.dom.id = "timeline-panel";
        this.resize();

        const ctx = trackCanvas.getContext("2d");

        if (!ctx) {
            throw new Error("Could not get 2D context from canvas");
        }

        this.ctx = ctx;

        const ctxWrap = proxyCtx(ctx);

        this.ctxWrap = ctxWrap;

        this.repaint();

        document.addEventListener("mousemove", this.onMouseMove.bind(this));

        trackCanvas.addEventListener("mouseout", () => {
            this.pointer = null;
        });

        handleDrag(
            trackCanvas,
            (e: Pointer) => {
                this.mousedown2 = true;

                this.pointer = {
                    x: e.offsetx || 0,
                    y: e.offsety || 0
                };

                this.pointerEvents();

                if (!this.mousedownItem) {
                    this.dispatcher.fire("time.update", TimelinePanel.xToTime(e.offsetx || 0));
                }
                // Hit criteria
            },
            (e: Pointer) => {
                this.mousedown2 = false;

                if (this.mousedownItem) {
                    this.mouseDownThenMove = true;

                    if (this.mousedownItem.mouseDrag) {
                        // @ts-ignore
                        this.mousedownItem.mouseDrag(e);
                    }
                } else {
                    this.dispatcher.fire("time.update", TimelinePanel.xToTime(e.offsetx || 0));
                }
            },
            (e: Pointer) => {
                if (this.mouseDownThenMove) {
                    this.dispatcher.fire("keyframe.move");
                } else {
                    this.dispatcher.fire("time.update", TimelinePanel.xToTime(e.offsetx || 0));
                }

                this.mousedown2 = false;
                this.mousedownItem = null;
                this.mouseDownThenMove = false;
            }
        );
    }

    scrollTo(s: number): void {
        this.scrollTop = s * Math.max(this.layers.length * LINE_HEIGHT - this.SCROLL_HEIGHT, 0);
        this.repaint();
    }

    resize(): void {
        const trackCanvasHeight =
            LayoutParameters.height - TIME_SCROLLER_HEIGHT - LayoutParameters.STATUS_BAR_HEIGHT - 5;

        const dpr = window.devicePixelRatio;

        const LEFT_GUTTER = LayoutParameters.LEFT_GUTTER;
        const RIGHT_GUTTER = LayoutParameters.RIGHT_GUTTER;
        const width = LEFT_GUTTER + this.videoRecorder.state.totalDuration * LayoutParameters.time_scale + RIGHT_GUTTER;

        this.trackCanvas.width = width * dpr;
        this.trackCanvas.height = trackCanvasHeight * dpr;
        this.trackCanvas.style.width = `${width}px`;
        this.trackCanvas.style.height = `${trackCanvasHeight}px`;
        this.SCROLL_HEIGHT = LayoutParameters.height - TIME_SCROLLER_HEIGHT;
    }

    repaint(): void {
        this.needsRepaint = true;
    }

    private drawTickMarkAreaBackground(): void {
        const width = LayoutParameters.width;

        this.ctx.fillStyle = "rgba(42, 42, 42, 1)";
        this.ctx.beginPath();
        this.ctx.moveTo(0, 0);
        this.ctx.lineTo(width, 0);
        this.ctx.lineTo(width, 40);
        this.ctx.lineTo(0, 40);

        this.ctx.closePath();
        this.ctx.fill();
    }

    private drawAnimationArea(): void {
        const height = LayoutParameters.height;

        const startX = TimelinePanel.timeToX(0);
        const endX = TimelinePanel.timeToX(this.dataStore.get("ui:totalTime").value);

        this.ctx.fillStyle = "rgba(66, 66, 66, 1)";
        this.ctx.beginPath();
        this.ctx.moveTo(startX, 40);
        this.ctx.lineTo(endX, 40);
        this.ctx.lineTo(endX, height);
        this.ctx.lineTo(startX, height);

        this.ctx.closePath();
        this.ctx.fill();
    }

    private drawLayerContents(): void {
        // horizontal Layer lines
        for (let i = 0, countOfLayers = this.layers.length; i <= countOfLayers; i++) {
            this.ctx.strokeStyle = Theme.b;
            this.ctx.beginPath();

            let y = i * LINE_HEIGHT;

            y = Math.floor(y) - 0.5;

            this.ctxWrap.moveTo(0, y).lineTo(LayoutParameters.width, y).stroke();
        }

        this.renderItems = [];

        const state = this.videoRecorder.state;

        const positionControlPoints = state.positionControlPoints;

        const totalTime = this.dataStore.get("ui:totalTime").value;

        for (let i = 0; i < positionControlPoints.length; i++) {
            const y = LINE_HEIGHT;

            const frame: Frame = {
                time: (totalTime / (positionControlPoints.length - 1)) * i,
                tween: "",
                value: 1
            };

            this.renderItems.push(
                new Diamond({
                    indexOfFrame: i,
                    frame: frame,
                    x: TimelinePanel.timeToX(frame.time),
                    y: y,
                    trackCanvas: this.trackCanvas,
                    ctx_wrap: this.ctxWrap,
                    dispatcher: this.dispatcher,
                    xToTime: TimelinePanel.xToTime.bind(this)
                })
            );
        }

        // render items
        let item: Diamond;

        for (let i = 0; i < this.renderItems.length; i++) {
            item = this.renderItems[i];
            item.paint(this.ctxWrap);
        }
    }

    private setTimeScale(): void {
        const v = this.dataStore.get("ui:timeScale").value;

        if (timeScale !== v) {
            timeScale = v;
            timeScaled();
        }
    }

    private check(): void {
        let item: Diamond;
        const lastOver = this.over;

        // over = [];
        this.over = null;

        const dpr = window.devicePixelRatio;

        for (let i = this.renderItems.length; i-- > 0; ) {
            item = this.renderItems[i];
            item.path(this.ctxWrap);

            if (this.pointer && this.ctx.isPointInPath(this.pointer.x * dpr, this.pointer.y * dpr)) {
                // over.push(item);
                this.over = item;
                break;
            }
        }

        // clear old mousein
        if (lastOver && lastOver !== this.over) {
            item = lastOver;

            if (item.mouseout) {
                item.mouseout();
            }
        }

        if (this.over) {
            item = this.over;

            if (item.mouseover) {
                item.mouseover();
            }

            if (this.mousedown2) {
                this.mousedownItem = item;
            }
        }
    }

    private pointerEvents(): void {
        if (!this.pointer) {
            return;
        }

        const dpr = window.devicePixelRatio;

        this.ctxWrap
            .save()
            .scale(dpr, dpr)
            .translate(0, MARKER_TRACK_HEIGHT)
            .beginPath()
            .rect(0, 0, LayoutParameters.width, this.SCROLL_HEIGHT)
            .translate(-this.scrollLeft, -this.scrollTop)
            .clip()
            .run(() => {
                this.check();
            })
            .restore();
    }

    _paint(): void {
        if (!this.needsRepaint) {
            this.pointerEvents();

            return;
        }

        this.setTimeScale();

        this.currentTime = this.dataStore.get("ui:currentTime").value;
        frameStart = this.dataStore.get("ui:scrollTime").value;

        /** *********************** */
        // background

        this.ctx.fillStyle = "rgba(51, 51, 51, 1)";
        this.ctx.clearRect(0, 0, this.trackCanvas.width, this.trackCanvas.height);
        this.ctx.save();

        const dpr = window.devicePixelRatio;

        this.ctx.scale(dpr, dpr);

        this.ctx.lineWidth = 1;

        const height = LayoutParameters.height;

        let units = timeScale / tickMark1;
        const offsetUnits = (frameStart * timeScale) % units;

        // let count = (width - LEFT_GUTTER + offsetUnits) / units;

        let count = Math.ceil(this.videoRecorder.state.totalDuration) + 1;

        // console.log('time_scale', time_scale, 'tickMark1', tickMark1, 'units', units, 'offsetUnits', offsetUnits, frame_start);

        // time_scale = pixels to 1 second (40)
        // tickMark1 = marks per second (marks / s)
        // units = pixels to every mark (40)

        this.drawAnimationArea();
        this.drawTickMarkAreaBackground();

        const LEFT_GUTTER = LayoutParameters.LEFT_GUTTER;

        // draw time labels only
        // 0:00 0:01 0:02
        for (let i = 0; i < count; i++) {
            const x = i * units + LEFT_GUTTER - offsetUnits;

            // vertical lines
            this.ctx.strokeStyle = Theme.b;
            this.ctx.beginPath();
            this.ctx.moveTo(x, 0);
            this.ctx.lineTo(x, height);
            this.ctx.stroke();

            this.ctx.fillStyle = Theme.d;
            this.ctx.textAlign = "center";

            const t = (i * units - offsetUnits) / timeScale + frameStart;

            this.ctx.fillText(utils.format_friendly_seconds(t), x, 38);
        }

        units = timeScale / tickMark2;
        // count = (width - LEFT_GUTTER + offsetUnits) / units;

        count = Math.ceil(this.videoRecorder.state.totalDuration) * tickMark2 + 1;

        // marker lines - main
        for (let i = 0; i < count; i++) {
            this.ctx.strokeStyle = Theme.c;
            this.ctx.beginPath();

            const x = i * units + LEFT_GUTTER - offsetUnits;

            this.ctx.moveTo(x, MARKER_TRACK_HEIGHT - 0);
            this.ctx.lineTo(x, MARKER_TRACK_HEIGHT - 16);
            this.ctx.stroke();
        }

        const mul = tickMark3 / tickMark2;

        units = timeScale / tickMark3;
        count = Math.ceil(this.videoRecorder.state.totalDuration) * tickMark3;

        // small ticks
        for (let i = 0; i < count; i++) {
            if (i % mul === 0) {
                continue;
            }

            this.ctx.strokeStyle = Theme.c;
            this.ctx.beginPath();

            const x = i * units + LEFT_GUTTER - offsetUnits;

            this.ctx.moveTo(x, MARKER_TRACK_HEIGHT - 0);
            this.ctx.lineTo(x, MARKER_TRACK_HEIGHT - 10);
            this.ctx.stroke();
        }

        // Encapsulate a scroll rect for the layers
        this.ctxWrap
            .save()
            .translate(0, MARKER_TRACK_HEIGHT)
            .beginPath()
            .rect(0, 0, LayoutParameters.width, this.SCROLL_HEIGHT)
            .translate(-this.scrollLeft, -this.scrollTop)
            .clip()
            .run(() => {
                this.drawLayerContents();
            })
            .restore();

        // Current Marker / Cursor
        this.ctx.strokeStyle = "red"; // Theme.c

        const x = (this.currentTime - frameStart) * timeScale + LayoutParameters.LEFT_GUTTER;

        const txt = utils.format_friendly_seconds(this.currentTime);
        const textWidth = this.ctx.measureText(txt).width;

        const baseLine = MARKER_TRACK_HEIGHT - 5;
        const halfRect = textWidth / 2 + 4;

        this.ctx.beginPath();
        this.ctx.moveTo(x, baseLine);
        this.ctx.lineTo(x, height);
        this.ctx.stroke();

        this.ctx.fillStyle = "red"; // black
        this.ctx.textAlign = "center";
        this.ctx.beginPath();
        this.ctx.moveTo(x, baseLine + 5);
        this.ctx.lineTo(x + 5, baseLine);
        this.ctx.lineTo(x + halfRect, baseLine);
        this.ctx.lineTo(x + halfRect, baseLine - 14);
        this.ctx.lineTo(x - halfRect, baseLine - 14);
        this.ctx.lineTo(x - halfRect, baseLine);
        this.ctx.lineTo(x - 5, baseLine);
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.fillStyle = "white";
        this.ctx.fillText(txt, x, baseLine - 4);

        this.ctx.restore();

        this.needsRepaint = false;
        // pointerEvents();
    }

    private yToTrack(y: number): number {
        if (y - MARKER_TRACK_HEIGHT < 0) {
            return -1;
        }

        return Math.floor((y - MARKER_TRACK_HEIGHT + this.scrollTop) / LINE_HEIGHT);
    }

    private static xToTime(x: number): number {
        const units = timeScale / tickMark3;

        // return frame_start + (x - LEFT_GUTTER) / time_scale;

        return frameStart + Math.floor((x - LayoutParameters.LEFT_GUTTER) / units) / tickMark3;
    }

    private static timeToX(s: number): number {
        let ds = s - frameStart;

        ds *= timeScale;
        ds += LayoutParameters.LEFT_GUTTER;

        return ds;
    }

    private onMouseMove(e: MouseEvent): void {
        this.canvasBounds = this.trackCanvas.getBoundingClientRect();

        const mx = e.clientX - this.canvasBounds.left;
        const my = e.clientY - this.canvasBounds.top;

        this.onPointerMove(mx, my);
    }

    private onPointerMove(x: number, y: number): void {
        if (this.mousedownItem) {
            return;
        }

        this.pointerDidMoved = true;
        this.pointer = { x: x, y: y };
    }

    setState(state: any): void {
        this.layers = state.value;
        this.repaint();
    }
}
